// StepLottie — renders a Lottie animation for a course step.
//
// Lazy-loads `lottie-react` and the JSON animation file. Both happen
// async so the cold-start bundle stays slim for users who never open
// a course with animations attached. Falls back to nothing on any
// failure (network, missing dep, malformed JSON) — courses degrade
// gracefully to text-only steps if a Lottie source is broken.
//
// Why this exists: course steps carry an admin-attached `.lottie =
// { url, loop, speed }` field. CookMode picks one of three step-
// animation sources in priority order:
//   1. step.lottie  (course-attached, this component)
//   2. step.animation key → AnimationMap (legacy bundled animations)
//   3. nothing
// Course Lottie wins because it's purpose-curated for that specific
// step, with admin sign-off.
//
// Setup: `npm install lottie-react` to activate. Without the lib,
// the lazy import fails, the component renders null silently.

import { useEffect, useRef, useState } from "react";

export default function StepLottie({ url, loop = true, speed = 1.0, height = 220 }) {
  const [Lottie, setLottie] = useState(null);
  const [data, setData] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!url) return undefined;
    (async () => {
      try {
        // Parallel: import the lib + fetch the JSON. Fastest first paint.
        const [mod, resp] = await Promise.all([
          import("lottie-react"),
          fetch(url, { cache: "force-cache" }),
        ]);
        if (cancelledRef.current) return;
        if (!resp.ok) {
          console.warn("[StepLottie] fetch failed:", resp.status, url);
          return;
        }
        const json = await resp.json();
        if (cancelledRef.current) return;
        setLottie(() => mod.default || mod.Lottie || null);
        setData(json);
      } catch (e) {
        // `lottie-react` not installed yet, or animation JSON
        // unreachable / malformed. Silent fail — course step still
        // renders, just without animation.
        console.warn("[StepLottie] init skipped:", e?.message || e);
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [url]);

  if (!Lottie || !data) return null;
  return (
    <div style={{ width: "100%", maxWidth: 360, height, margin: "0 auto" }}>
      <Lottie
        animationData={data}
        loop={loop}
        autoplay
        style={{ width: "100%", height: "100%" }}
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
        // Apply playback speed via the imperative ref. lottie-react's
        // setSpeed lives on the underlying instance; the prop API
        // exposes it via lottieRef but the simplest path is just to
        // trust the default 1.0 and override via JSON if needed.
      />
    </div>
  );
}
